import { AudioDeviceSelector } from "@/components/AudioDeviceSelector";
import { MicrophonePermissionRequest } from "@/components/MicrophonePermissionRequest";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AudioDevice, audioSettingsStorage } from "@/utils/audioSettings";
import { useMicrophonePermission } from "@/utils/microphonePermissions";
import { Mic } from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

const AudioSettings: React.FC = () => {
  const { isGranted: microphoneGranted, checkPermission } =
    useMicrophonePermission();
  const [selectedAudioDevice, setSelectedAudioDevice] =
    useState<AudioDevice | null>(null);

  useEffect(() => {
    const savedDevice = audioSettingsStorage.getSelectedDevice();
    setSelectedAudioDevice(savedDevice);
  }, []);

  const handleAudioDeviceSelected = (device: AudioDevice) => {
    setSelectedAudioDevice(device);
  };

  const handleClearAudioSettings = () => {
    audioSettingsStorage.clear();
    setSelectedAudioDevice(null);
    toast.success("Audio settings cleared successfully");
  };

  return (
    <>
      <title>Audio Settings</title>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Mic className="h-5 w-5" />
              <CardTitle>Microphone</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="@container space-y-6">
            {!microphoneGranted ? (
              <MicrophonePermissionRequest
                onPermissionGranted={() => {
                  checkPermission();
                  toast.success(
                    "Microphone access granted! You can now configure your audio settings.",
                  );
                }}
                onPermissionDenied={(result) => {
                  toast.error(result.message);
                }}
              />
            ) : (
              <div className="space-y-6">
                {selectedAudioDevice && (
                  <div className="flex flex-col gap-6 @md:flex-row @md:items-center @md:justify-between">
                    <div className="space-y-1">
                      <h3 className="font-medium">Current Device</h3>
                      <p className="text-sm text-muted-foreground">
                        {selectedAudioDevice.label}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleClearAudioSettings}
                    >
                      Clear Settings
                    </Button>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="space-y-1">
                    <h3 className="font-medium">Select Microphone</h3>
                    <p className="text-sm text-muted-foreground">
                      Choose your preferred microphone for audio recording. This
                      setting will be remembered for future recordings.
                    </p>
                  </div>
                  <AudioDeviceSelector
                    onDeviceSelected={handleAudioDeviceSelected}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default AudioSettings;
